import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { getModifiers } from '@/shared/ui/core';
import IconCheck from '@/shared/assets/icon-check.svg';

import { Checkbox, CheckField, IconContainer } from './Checkbox.styled';

const modifierProps = ['radial', 'disabled', 'deactivated', 'large'];

// FIXME: This component includes a workaround for Safari,
// where the onChange event for a checkbox inside a <dialog>
// does not get fired consistently.

const enqueue = (iterations, func) => {
  if (!iterations) {
    func();
  } else {
    setTimeout(() => enqueue(iterations - 1, func));
  }
};

export default React.forwardRef(({ name, children, label, ...props }, ref) => {
  const { setFieldValue } = useFormikContext();
  const onChangeCalled = React.useRef(false);

  const onChange = React.useCallback((event) => {
    onChangeCalled.current = true;
    if (!props.onChange) {
      setFieldValue(event.target.name, event.target.checked);
    } else {
      props.onChange(event);
    }

    enqueue(2, () => {
      onChangeCalled.current = false;
    });
  }, [props.onChange]);

  const onLabelClick = React.useCallback(() => {
    enqueue(1, () => {
      const checkbox = document.querySelector(`input[name="${name}"]`);
      if (!onChangeCalled.current) {
        checkbox.checked = !checkbox.checked;
        props.onChange({ target: checkbox });
      }
    });
  }, [props.onChange]);

  return (
    <Checkbox
      name={name}
      onClick={onLabelClick}
      modifiers={getModifiers(props, modifierProps)}
    >
      <CheckField
        name={name}
        type="checkbox"
        role="checkbox"
        innerRef={ref}
        {...props}
        onChange={onChange}
      />
      <IconContainer>
        <IconCheck />
      </IconContainer>
      {label}
    </Checkbox>
  );
});

Checkbox.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  modifiers: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.string,
};

Checkbox.defaultProps = {
  label: '',
  modifiers: '',
  disabled: '',
};
